import React, { useMemo, useState } from 'react';
import { themes } from '../config/themes';
import { getLocalDateKey, parseEventDate } from '../utils/eventUtils';
import SkylineWatermark from './calendar/SkylineWatermark';
import CalendarLegend from './calendar/CalendarLegend';
import { CATEGORIES, catColor, categorizeEvent } from '../config/categories';
import { fs, sz } from '../utils/typography';

const DOW_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const START_HOUR = 7;
const END_HOUR = 22;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const GUTTER_W = 52;
const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => START_HOUR + i);

const fmtHour = (h) => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const fmtTime = (date) => date
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';

const getEventTimeRange = (event) => {
    const start = parseEventDate(event.startDate);
    if (!start) return null;
    const end = parseEventDate(event.endDate || event.startDate);
    const startH = start.getHours() + start.getMinutes() / 60;
    const endH = end && end.getTime() !== start.getTime()
        ? end.getHours() + end.getMinutes() / 60
        : startH + 1;
    return { start, end, startH, endH };
};

const isAllDayEvent = (event) => {
    const range = getEventTimeRange(event);
    return !range || (range.start.getHours() === 0 && range.start.getMinutes() === 0);
};

const isOutsideVisibleHours = (event) => {
    const range = getEventTimeRange(event);
    return range && (range.endH <= START_HOUR || range.startH >= END_HOUR);
};

// Returns top/height as percentages for a timed event within the hour grid.
const getBlock = (event) => {
    const range = getEventTimeRange(event);
    if (!range || range.startH === 0) return null; // treat midnight as all-day
    const { startH, endH } = range;
    const clampedStart = Math.max(startH, START_HOUR);
    const clampedEnd = Math.min(Math.max(endH, startH + 0.5), END_HOUR);
    if (clampedEnd <= clampedStart) return null;
    return {
        top: ((clampedStart - START_HOUR) / TOTAL_HOURS) * 100,
        height: ((clampedEnd - clampedStart) / TOTAL_HOURS) * 100,
        startH,
        endH,
    };
};

// Assigns non-overlapping column slots to events within one day.
const resolveColumns = (dayEvents) => {
    const items = dayEvents
        .map((event) => ({ event, block: getBlock(event) }))
        .filter((x) => x.block);

    items.sort((a, b) => a.block.startH - b.block.startH);

    const clusters = [];
    items.forEach((item) => {
        const last = clusters[clusters.length - 1];
        if (!last || item.block.startH >= last.endH - 0.05) {
            clusters.push({ items: [item], endH: item.block.endH });
            return;
        }
        last.items.push(item);
        last.endH = Math.max(last.endH, item.block.endH);
    });

    const positioned = clusters.flatMap((cluster) => {
        const slots = []; // slots[i] = endH of last event in column i
        const result = cluster.items.map(({ event, block }) => {
            let col = 0;
            while (slots[col] !== undefined && slots[col] > block.startH + 0.05) col++;
            slots[col] = block.endH;
            return { event, block, col };
        });
        const totalCols = Math.max(1, ...result.map((r) => r.col + 1));
        return result.map((item) => ({ ...item, totalCols }));
    });

    return { positioned };
};

// Week view — "Atlanta Editorial" design refresh: charcoal day header, scrollable
// hour-grid with time gutter and duration-positioned event blocks, all-day row,
// skyline watermark, legend footer.
const WeekView2 = ({ days, events, theme, onDaySelect, onEventSelect, selectedDay }) => {
    const tokens = theme?.tokens || themes.light.tokens;
    const c = tokens.colors;
    const [hoveredEventId, setHoveredEventId] = useState(null);

    const { allDayByDay, timedByDay, outOfRangeByDay } = useMemo(() => {
        const allDayByDay = {};
        const timedByDay = {};
        const outOfRangeByDay = {};
        (events || []).forEach((event) => {
            const key = getLocalDateKey(event.startDate);
            if (!key) return;
            if (isAllDayEvent(event)) {
                (allDayByDay[key] = allDayByDay[key] || []).push(event);
            } else if (isOutsideVisibleHours(event)) {
                (outOfRangeByDay[key] = outOfRangeByDay[key] || []).push(event);
            } else {
                (timedByDay[key] = timedByDay[key] || []).push(event);
            }
        });
        return { allDayByDay, timedByDay, outOfRangeByDay };
    }, [events]);

    const todayKey = getLocalDateKey(new Date());
    const selectedKey = selectedDay ? getLocalDateKey(selectedDay) : null;

    // Current time indicator
    const now = new Date();
    const nowFrac = now.getHours() + now.getMinutes() / 60;
    const nowTop = ((nowFrac - START_HOUR) / TOTAL_HOURS) * 100;
    const showNow = nowFrac >= START_HOUR && nowFrac < END_HOUR;
    const todayInWeek = days.some((d) => getLocalDateKey(d) === todayKey);

    const handleEventClick = (event, day, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (onDaySelect) onDaySelect(day);
        if (onEventSelect) onEventSelect(event, e);
    };

    return (
        <div
            className="w-full h-full flex flex-col overflow-hidden"
            style={{ background: c.surface, color: c.ink, fontFamily: tokens.fonts.ui, textAlign: 'left' }}
            role="region"
            aria-label="Week calendar"
        >
            {/* main content — skyline anchored here (same scope as month grid) */}
            <div className="flex-1 min-h-0 flex flex-col relative overflow-hidden">
            <SkylineWatermark tokens={tokens} />
            {/* charcoal day header band */}
            <div
                className="grid flex-none"
                style={{
                    gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`,
                    background: c.headBg, borderBottom: `1px solid ${c.gridLine}`,
                    position: 'relative', zIndex: 2,
                }}
                role="row"
            >
                <div /> {/* gutter spacer */}
                {days.map((day) => {
                    const dateKey = getLocalDateKey(day);
                    const isHighlighted = dateKey === (selectedKey || todayKey);
                    return (
                        <div
                            key={dateKey}
                            className="flex flex-col lg:flex-row items-center lg:items-baseline justify-center lg:justify-start gap-1 lg:gap-2 py-2 lg:py-3 px-1 lg:px-3 cursor-pointer"
                            onClick={() => onDaySelect(day)}
                            style={{ borderLeft: '1px solid rgba(255,255,255,0.08)' }}
                            role="columnheader"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDaySelect(day); } }}
                            aria-label={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        >
                            <span style={{ fontFamily: tokens.fonts.ui, fontSize: fs(11), fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', color: isHighlighted ? c.accent : c.headInk }}>
                                {DOW_SHORT[day.getDay()]}
                            </span>
                            <span style={{ fontFamily: tokens.fonts.display, fontSize: fs(22), fontWeight: 500, lineHeight: 1, color: isHighlighted ? c.accent : c.headInk }}>
                                {day.getDate()}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* all-day row */}
            <div
                className="grid flex-none"
                style={{
                    gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`,
                    borderBottom: `1px solid ${c.gridLine}`,
                    background: c.surfaceAlt,
                    height: sz(96),
                }}
            >
                <div style={{
                    fontFamily: tokens.fonts.mono, fontSize: fs(9), letterSpacing: 0.5, textTransform: 'uppercase',
                    color: c.faint, padding: '9px 6px 0', textAlign: 'right', alignSelf: 'stretch',
                    borderRight: `1px solid ${c.gridLine}`,
                }}>
                    All day
                </div>
                {days.map((day) => {
                    const dateKey = getLocalDateKey(day);
                    const allDayEvents = allDayByDay[dateKey] || [];
                    const outOfRangeEvents = outOfRangeByDay[dateKey] || [];
                    return (
                        <div
                            key={dateKey}
                            style={{
                                borderLeft: `1px solid ${c.gridLine}`, padding: '5px 4px',
                                display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto',
                            }}
                        >
                            {[...allDayEvents, ...outOfRangeEvents].map((event) => {
                                const cat = categorizeEvent(event);
                                const cc = catColor(cat, tokens);
                                const outOfRange = outOfRangeEvents.includes(event);
                                return (
                                    <div
                                        key={event.id}
                                        onClick={(e) => handleEventClick(event, day, e)}
                                        style={{
                                            borderLeft: `3px solid ${cc.strong}`,
                                            borderTop: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            borderRight: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            borderBottom: outOfRange ? `1px dashed ${cc.line}` : `1px solid ${cc.line}`,
                                            background: cc.fillBg,
                                            borderRadius: 5, padding: '4px 7px', cursor: 'pointer',
                                            fontFamily: tokens.fonts.ui, fontSize: fs(14), fontWeight: 600,
                                            color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            lineHeight: 1.25,
                                        }}
                                        title={outOfRange ? `${event.title} — outside visible hours` : event.title}
                                    >
                                        {outOfRange && (
                                            <span style={{ fontFamily: tokens.fonts.mono, fontSize: fs(11.5), color: cc.strong, marginRight: 4 }}>
                                                {fmtTime(parseEventDate(event.startDate))}
                                            </span>
                                        )}
                                        <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: fs(10.5), marginRight: 3 }} aria-hidden="true" />
                                        {event.eventType === 'Featured' && (
                                            <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(12), marginRight: 4 }} aria-label="Featured event" />
                                        )}
                                        {event.title}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {/* scaled hour grid */}
            <div className="flex-1 min-h-0 overflow-hidden" style={{ position: 'relative' }}>
                <div
                    className="grid"
                    style={{ gridTemplateColumns: `${GUTTER_W}px repeat(7, 1fr)`, height: '100%', position: 'relative', zIndex: 1 }}
                    role="grid"
                >
                    {/* time gutter */}
                    <div style={{
                        position: 'relative', height: '100%', borderRight: `1px solid ${c.gridLine}`,
                        background: `color-mix(in srgb, ${c.surface} 50%, transparent)`,
                    }}>
                        {hours.map((h) => (
                            <div
                                key={h}
                                style={{
                                    position: 'absolute', top: `${((h - START_HOUR) / TOTAL_HOURS) * 100}%`,
                                    right: 8, transform: 'translateY(-50%)',
                                    fontFamily: tokens.fonts.mono, fontSize: fs(9.5), letterSpacing: 0.3,
                                    color: c.faint, fontWeight: 500, whiteSpace: 'nowrap',
                                }}
                                aria-hidden="true"
                            >
                                {fmtHour(h)}
                            </div>
                        ))}
                    </div>

                    {/* day columns */}
                    {days.map((day, i) => {
                        const dateKey = getLocalDateKey(day);
                        const dayEvents = timedByDay[dateKey] || [];
                        const isSelected = dateKey === selectedKey;
                        const isToday = dateKey === todayKey;
                        const { positioned } = resolveColumns(dayEvents);

                        return (
                            <div
                                key={dateKey}
                                style={{
                                    borderLeft: `1px solid ${c.gridLine}`,
                                    background: isSelected
                                        ? `color-mix(in srgb, ${c.selBg} 50%, transparent)`
                                        : `color-mix(in srgb, ${c.surface} 50%, transparent)`,
                                    boxShadow: isSelected ? `inset 0 0 0 1.5px ${c.selRing}` : 'none',
                                    position: 'relative', height: '100%', cursor: 'pointer',
                                }}
                                onClick={() => onDaySelect(day)}
                                role="gridcell"
                                aria-label={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            >
                                {/* hour lines */}
                                {hours.map((h) => (
                                    <div
                                        key={h}
                                        style={{
                                            position: 'absolute', top: `${((h - START_HOUR) / TOTAL_HOURS) * 100}%`,
                                            left: 0, right: 0,
                                            borderTop: `1px solid ${c.gridLine}`,
                                            opacity: 0.6,
                                        }}
                                        aria-hidden="true"
                                    />
                                ))}

                                {/* current time indicator */}
                                {showNow && isToday && todayInWeek && (
                                    <div
                                        style={{ position: 'absolute', top: `${nowTop}%`, left: 0, right: 0, zIndex: 4, pointerEvents: 'none' }}
                                        aria-hidden="true"
                                    >
                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 8, height: 8, borderRadius: 4, background: c.accent, marginLeft: -4, flex: '0 0 auto' }} />
                                            <div style={{ flex: 1, height: 1.5, background: c.accent }} />
                                        </div>
                                    </div>
                                )}

                                {/* timed events */}
                                {positioned.map(({ event, block, col, totalCols }) => {
                                    const cat = categorizeEvent(event);
                                    const cc = catColor(cat, tokens);
                                    const dark = tokens.mode === 'dark';
                                    const colW = 100 / totalCols;
                                    const colLeft = col * colW;
                                    const isHovered = hoveredEventId === event.id;
                                    const isShort = block.height < 48;
                                    return (
                                        <div
                                            key={event.id}
                                            onClick={(e) => handleEventClick(event, day, e)}
                                            onMouseOver={() => setHoveredEventId(event.id)}
                                            onMouseEnter={() => setHoveredEventId(event.id)}
                                            onMouseMove={() => setHoveredEventId(event.id)}
                                            onMouseLeave={() => setHoveredEventId(null)}
                                            onFocus={() => setHoveredEventId(event.id)}
                                            onBlur={() => setHoveredEventId(null)}
                                            style={{
                                                position: 'absolute',
                                                top: `calc(${block.top}% + 1px)`,
                                                height: `calc(${block.height}% - 2px)`,
                                                minHeight: sz(28),
                                                left: isHovered ? 3 : `calc(${colLeft}% + 3px)`,
                                                width: isHovered ? 'calc(100% - 6px)' : `calc(${colW}% - 6px)`,
                                                background: `color-mix(in srgb, ${dark ? c.surfaceAlt : c.surface} 94%, transparent)`,
                                                borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                                                borderLeft: `3px solid ${cc.strong}`,
                                                padding: isShort ? '3px 7px' : '6px 8px',
                                                zIndex: isHovered ? 8 : 3,
                                                boxShadow: isHovered
                                                    ? `0 5px 18px ${dark ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.18)'}`
                                                    : `0 1px 5px rgba(0,0,0,0.09)`,
                                                transition: 'left 150ms ease, width 150ms ease, box-shadow 150ms ease',
                                            }}
                                            title={`${event.title} — ${fmtTime(parseEventDate(event.startDate))}`}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(event, day, e); } }}
                                            aria-label={`${event.title} at ${fmtTime(parseEventDate(event.startDate))}`}
                                        >
                                            <div style={{
                                                fontFamily: tokens.fonts.ui, fontSize: isShort ? fs(13.5) : fs(15), fontWeight: 700, color: c.ink,
                                                lineHeight: isShort ? 1.12 : 1.25, overflow: 'hidden',
                                                textOverflow: 'ellipsis', whiteSpace: isShort ? 'nowrap' : 'normal',
                                                display: isShort ? 'block' : '-webkit-box',
                                                WebkitLineClamp: isShort ? 'unset' : 2, WebkitBoxOrient: 'vertical',
                                            }}>
                                                <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: isShort ? fs(10) : fs(11), marginRight: 3 }} aria-hidden="true" />
                                                {event.eventType === 'Featured' && (
                                                    <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(12), marginRight: 4 }} />
                                                )}
                                                {event.title}
                                            </div>
                                            <div style={{
                                                fontFamily: tokens.fonts.ui, fontSize: isShort ? fs(12) : fs(13.5), fontWeight: 600,
                                                color: c.muted, lineHeight: isShort ? 1.08 : 1.18,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                marginTop: isShort ? 1 : 3,
                                            }}>
                                                {event.organization}
                                            </div>
                                            <div style={{
                                                fontFamily: tokens.fonts.mono, fontSize: isShort ? fs(9.5) : fs(10.5), fontWeight: 600,
                                                letterSpacing: 0.2, color: cc.strong, whiteSpace: 'nowrap',
                                                overflow: 'hidden', textOverflow: 'ellipsis',
                                                marginTop: isShort ? 1 : 3,
                                            }}>
                                                {fmtTime(parseEventDate(event.startDate))}
                                            </div>
                                            {event.sponsorLogo && (
                                                <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <img
                                                        src={event.sponsorLogo}
                                                        alt={event.sponsorName || 'Sponsor'}
                                                        style={{ maxHeight: 16, maxWidth: '80%', objectFit: 'contain', opacity: 0.85 }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
            </div>

            <CalendarLegend tokens={tokens} />
        </div>
    );
};

export default WeekView2;
